import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator'
import { List } from '@/models/list/list';
import { DeliveryServer } from '@/models/delivery-server/delivery-server';
import { TrackingDomain } from '@/models/tracking-domain/tracking-domain';
import { IMailWizzEntity } from '@/models/common/mailwizz-entity';
import { INamedEntity } from '@/models/common/named-entity';
import { Campaign } from '@/models/campaign/campaign';
import { isStringNull } from '@/shared/utils/utils';
import { TestCase } from '@/models/test-case/testcase';
import { uniqueNames } from './unique-names';
import { TestCaseResponse } from '@/models/test-case/test-case-response';
import { testCaseService } from '@/services/testcase-service';
import { Settings } from '@/models/settings/settings';
import { TESTCASE_SERVICES, TESTCASE_PROVIDERS } from '@/constants/testcase-options';
import { googleSheetsService } from '@/services/google-sheets-service';

@Component
export default class TestcaseGeneratorComponent extends Vue {

    readonly selectListFormatter = (entity: IMailWizzEntity & INamedEntity) => { return { text: entity.name, value: entity } }
    readonly deliveryServerListFormatter = (entity: DeliveryServer) => { return { text: entity.name, html: this.formatDeliveryServerOption(entity), value: entity } }
    readonly trackingDomainListFormatter = (entity: TrackingDomain) => { return { text: entity.name, html: this.formatTrackingDomainOption(entity), value: entity } }
    
    @Prop() lists: List[]
    @Prop() deliveryServers: DeliveryServer[]
    @Prop() trackingDomains: TrackingDomain[]
    @Prop() copyFrom: TestCaseResponse
    @Prop() settings: Settings
    @Prop() usedTrackingDomains: number[]

    @Emit('generated')
    notifyTetstCaseGenerated(testCase: TestCase) { }

    testcaseName: string = null
    selectedList: List = null
    subjects: any[] = [ { value: null, index: 0 } ]
    bodies: any[] = [ { value: null, index: 0 } ]
    selectedDeliveryServers: DeliveryServer[] = [];
    selectedTrackingDomains: TrackingDomain[] = [];
    trackingDomainsMode: string = 'delivery-server'
    selectedService: string = 'glock'
    selectedProviders: number[] = [59, 42]  // gmail and gsuite
    domainDataLoaded: boolean = false;

    invalidFields: string[] = []
    testCaseNameValidation: string = null
    generating: boolean = false

    readonly services = TESTCASE_SERVICES
    readonly providerOptions = TESTCASE_PROVIDERS

    listNameValidation: string = null
    providersLocked: boolean = false

    getPriorityGroup(targetPriority: number | null): DeliveryServer[] {
    const servers = this.deliveryServers.filter(x => x.status.toLowerCase() != 'disabled' && x.name.toLowerCase().indexOf('do not use') < 0 && x.name.toLowerCase().indexOf('do no use') < 0 && x.name.indexOf('#') < 0);
    
    if (!this.domainDataLoaded) {
        return targetPriority === null ? servers : [];
    }
    
    return servers.filter(server => {
        const priority = googleSheetsService.getDomainPriority(server.name);
        return priority === targetPriority;
    });
    }

    get filteredTrackingDomains() {
        if (this.settings.hideDefaultTrackingDomains) {
            return this.trackingDomains.filter(td => {
                return !this.deliveryServers.some(ds => ds.trackingDomainId == td.mailWizzEntityId);
            });
        }

        return this.trackingDomains;
    }

    async mounted() {
    // Fetch domain priorities from Google Sheets
    await googleSheetsService.getDomainPriorities();
    
    // Trigger reactivity by updating a data property
    this.domainDataLoaded = true;
    
    // Force re-render after a small delay to ensure data is processed
    setTimeout(() => {
        this.$forceUpdate();
    }, 100);

    // Existing copy logic (if copyFrom exists)
    if (this.copyFrom) {
        this.testcaseName = `${this.copyFrom.name} COPY`
        this.selectedList = this.lists.find(x => x.mailWizzEntityId == this.copyFrom.listId)
        this.subjects = this.copyFrom.subjects.map((x, index) => { return { value: x, index: index} })
        this.bodies = this.copyFrom.bodies.map((x, index) => { return { value: x, index: index} })
        this.selectedDeliveryServers = this.deliveryServers.filter(x => this.copyFrom.deliveryServers.indexOf(x.mailWizzEntityId) >= 0)
        this.selectedTrackingDomains = this.trackingDomains.filter(x => this.copyFrom.trackingDomains.indexOf(x.mailWizzEntityId) >= 0)
        this.trackingDomainsMode = this.copyFrom.useDeliveryServerTrackingDomain ? 'delivery-server' : 'manual'
    }
    }

    addSubjectRow() {
        const maxIndex = Math.max(...this.subjects.map(x => x.index))
        this.subjects.push({ value: null, index: maxIndex + 1 });
        Vue.nextTick(() => {
            this.$refs.subjects[this.subjects.length - 1].focus()
        })
    }

    removeSubjectRow(index: number) {
        const arrayIndex = this.subjects.findIndex(x => x.index == index)
        this.subjects.splice(arrayIndex, 1)
    }

    addBodyRow() {
        const maxIndex = Math.max(...this.bodies.map(x => x.index))
        this.bodies.push({ value: null, index: maxIndex + 1 });
        Vue.nextTick(() => {
            this.$refs.bodies[this.bodies.length - 1].focus()
        })
    }

    removeBodyRow(index: number) {
        const arrayIndex = this.bodies.findIndex(x => x.index == index)
        this.bodies.splice(arrayIndex, 1)
    }

    getTestcaseCombinations() {
        const combinations = [];

        for (let subjInx = 0; subjInx < this.subjects.length; subjInx ++) {
            const subject = this.subjects[subjInx].value ? this.subjects[subjInx].value.trim() : null
            if (!subject) continue

            for (let bodyInx = 0; bodyInx < this.bodies.length; bodyInx ++) {
                const body = this.bodies[bodyInx].value ? this.bodies[bodyInx].value.trim() : null
                if (!body) continue

                for (let delServIdx = 0; delServIdx < this.selectedDeliveryServers.length; delServIdx++) {
                    const deliveryServer = this.selectedDeliveryServers[delServIdx]
    
                    const trackingDomains = this.trackingDomainsMode == 'manual'
                        ? this.selectedTrackingDomains
                        : [this.trackingDomains.find(x => x.mailWizzEntityId == deliveryServer.trackingDomainId)]
                    
                    for (let trackDomIdx = 0; trackDomIdx < trackingDomains.length; trackDomIdx++) {
                        const trackingDomain = trackingDomains[trackDomIdx];

                        combinations.push({
                            subject,
                            body,
                            deliveryServer,
                            trackingDomain
                        })
                    }
                }
            }
        }

        return combinations;
    }

    async generateTestcase() {
        
        this.generating = true;
        try {
            if (!(await this.validate())) return
    
            const campaigns: Campaign[] = []
            const usedNames: string[] = []
    
            const combinations = this.getTestcaseCombinations();
            combinations.forEach(combination => {
                const campaign = new Campaign()
        
                const newUniqueName = this.getNewUniqueName(usedNames)
                usedNames.push(newUniqueName)

                campaign.subject = `${newUniqueName}, ${combination.subject}`
                campaign.body = combination.body
                campaign.list = this.selectedList
                campaign.deliveryServer = combination.deliveryServer
                campaign.trackingDomain = combination.trackingDomain
                campaign.fromName = combination.deliveryServer.fromName
                campaign.fromEmail = combination.deliveryServer.fromEmail
                campaign.replyTo = combination.deliveryServer.replyTo
                campaign.service = this.selectedService
                campaign.providers = this.selectedProviders

                campaigns.push(campaign)
            });
    
            const testCase = new TestCase()
            testCase.name = this.testcaseName
            testCase.campaigns = campaigns
            testCase.listId = this.selectedList.mailWizzEntityId
            testCase.subjects = this.subjects.map(x => x.value)
            testCase.bodies = this.bodies.map(x => x.value)
            testCase.deliveryServers = this.selectedDeliveryServers.map(x => x.mailWizzEntityId)
            testCase.trackingDomains = this.selectedTrackingDomains.map(x => x.mailWizzEntityId)
            testCase.useDeliveryServerTrackingDomain = this.trackingDomainsMode == 'delivery-server'
    
            this.notifyTetstCaseGenerated(testCase)
        }
        finally {
            this.generating = false
        }
    }

    get potentialNumberOfTestcases() {
        return this.getTestcaseCombinations().length;
    }

    async validate() : Promise<boolean> {
        this.clearValidation()

        if (isStringNull(this.testcaseName)) {
            this.invalidFields.push('testcaseName')
        }
        else {
            if (await testCaseService.nameIsUsed(this.testcaseName)) {
                this.invalidFields.push('testcaseName')
                this.testCaseNameValidation = 'Specified Test Case name is already taken'
            }
        }

        if (!this.selectedList || isStringNull(this.selectedList.mailWizzEntityId)) {
            this.invalidFields.push('list')
        }
        this.subjects.forEach(x => {
            if (isStringNull(x.value)) {
                this.invalidFields.push(`subject${x.index}`)
            }
        })
        this.bodies.forEach(x => {
            if (isStringNull(x.value)) {
                this.invalidFields.push(`body${x.index}`)
            }
        })
        if (this.selectedDeliveryServers.length == 0) {
            this.invalidFields.push('deliveryServers')
        }
        if (this.selectedTrackingDomains.length == 0 && this.trackingDomainsMode == 'manual') {
            this.invalidFields.push('trackingDomains')
        }
        if (this.selectedService === 'glock' && this.selectedProviders.length === 0) {
            this.invalidFields.push('providers')
        }

        if (this.trackingDomainsMode == 'delivery-server' && this.invalidFields.length == 0) {
            const deliveryServers = this.selectedDeliveryServers.
                filter(x => this.usedTrackingDomains.indexOf(x.trackingDomainId) >= 0);
            if (deliveryServers.length > 0) {
                const message = [
                    this.$createElement('div', "Some of the selected Delivery Servers are using Tracking Domains that are marked as 'Locked':"),
                    this.$createElement('ul', { attrs: { class: 'used-td-list-warning' } }, deliveryServers.map(x => this.$createElement('li', `${x.name} (${this.trackingDomains.find(td => td.mailWizzEntityId == x.trackingDomainId).name})`)))
                ];
                if (!(await this.$confirm(message))) {
                    return false;
                }
            }
        }

        return this.invalidFields.length == 0
    }

    clearValidation() {
        if (this.invalidFields.length) {
            this.invalidFields.splice(0, this.invalidFields.length)
        }
        this.testCaseNameValidation = null
    }

    getValidationState(fieldname: string) {
        return this.invalidFields.indexOf(fieldname) >= 0 ? false : null
    }

    getNewUniqueName(usedNames: string[]) : string {
        const availableNames = uniqueNames.filter(x => usedNames.indexOf(x) < 0)
        return availableNames[Math.floor(Math.random() * availableNames.length)]
    }

    formatDeliveryServerOption(deliveryServer: DeliveryServer) {
    // Get individual server usage count (not ESP-wide)
    const usedCount = deliveryServer.usedRecently || 0;

    // Get priority for background color (but don't show emoji)
    const priority = googleSheetsService.getDomainPriority(deliveryServer.name);

    const usedCountText = usedCount > 0 
        ? `<span class="list-additional-info text-info">${usedCount}</span>` 
        : '';

    // Desaturated versions of your original colors
    let backgroundColor = '';
    let textDecoration = '';
    
    switch (priority) {
        case 1:
            backgroundColor = 'background-color: #c8e6c9;'; // Desaturated light green
            break;
        case 2:
            backgroundColor = 'background-color: #fff8dc;'; // Desaturated light gold
            break;
        case 3:
            backgroundColor = 'background-color: #f5deb3;'; // Desaturated light salmon
            break;
        default:
            backgroundColor = 'background-color: #f0d0d0;'; // Desaturated light pink
            textDecoration = 'text-decoration: line-through;';
            break;
    }

    // Return server name with background color but NO emoji numbers
    return `<span style="${backgroundColor} ${textDecoration}">${deliveryServer.name}</span> ${usedCountText}`;
    }

    formatTrackingDomainOption(trackingDomain: TrackingDomain) {
        const usedCount = this.usedTrackingDomains.filter(x => x == trackingDomain.mailWizzEntityId).length;
        return usedCount > 0
            ? `${trackingDomain.name} <small><i class="fa fa-lock ml-1" style="opacity:0.75"/></small>`
            : trackingDomain.name;
    }

    parseProviderIdsFromListName(listName: string): number[] {
        const match = listName.match(/\[([\d,]+)\]/);
        if (!match) return null;
        
        try {
            return match[1].split(',').map(id => parseInt(id.trim()));
        } catch {
            return null;
        }
    }

    validateProviderIds(ids: number[]): boolean {
        if (!ids) return false;
        return ids.every(id => this.providerOptions.some(option => option.value === id));
    }

    @Watch('selectedList')
    onListChange(newList: List) {
        if (!newList) {
            this.listNameValidation = null;
            this.providersLocked = false;
            return;
        }

        const providerIds = this.parseProviderIdsFromListName(newList.name);
        
        if (this.selectedService === 'glock') {
            if (!providerIds) {
                this.listNameValidation = 'List name should contain provider IDs in square brackets (e.g. [3,17])';
                this.providersLocked = false;
            } else if (!this.validateProviderIds(providerIds)) {
                this.listNameValidation = 'List contains invalid provider IDs';
                this.providersLocked = false;
            } else {
                this.listNameValidation = null;
                this.selectedProviders = providerIds;
                this.providersLocked = true;
            }
        }
    }

    @Watch('selectedService')
    onServiceChange(newService: string) {
        if (newService === 'glock' && this.selectedList) {
            this.onListChange(this.selectedList);
        } else {
            this.listNameValidation = null;
            this.providersLocked = false;
        }
    }
}
